import { useEffect } from 'react'
import type { PropsWithChildren } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  identifyLogRocketUser,
  initLogRocket,
  isInternalUserEmail,
  isLogRocketEnabled,
} from '@/lib/observability/logrocket'

/**
 * Initializes LogRocket (prod-only, gated by env) once Auth0 has resolved, then
 * keeps the identified user in sync. Internal @virtualscienceai.com users are
 * excluded entirely — init is skipped so no session is recorded. No-op when
 * disabled — safe to mount in every environment.
 */
export function LogRocketProvider({ children }: PropsWithChildren): JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth0()

  useEffect(() => {
    if (!isLogRocketEnabled() || isLoading) return
    if (isAuthenticated && isInternalUserEmail(user?.email)) return

    initLogRocket()

    if (isAuthenticated && user?.sub) {
      identifyLogRocketUser({
        id: user.sub,
        name: user.name ?? null,
        email: user.email ?? null,
      })
    } else if (!isAuthenticated) {
      identifyLogRocketUser(null)
    }
  }, [isAuthenticated, isLoading, user])

  return <>{children}</>
}
